import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Connection,
  Edge,
  Node,
  BackgroundVariant,
  useReactFlow,
  useUpdateNodeInternals,
  Panel,
  MarkerType,
  NodeMouseHandler,
  OnSelectionChangeParams,
  ConnectionMode,
  Position,
} from 'reactflow';
import { MiniMap } from 'reactflow';
import Sidebar from './components/Sidebar';
import CustomNode from './components/CustomNode';
import CustomEdge from './components/CustomEdge';
import PropertiesPanel from './components/PropertiesPanel';
import Toolbar, { Tool } from './components/Toolbar';
import DrawingCanvas from './components/DrawingCanvas';
import ContextMenu from './components/ContextMenu';
import NodeContextMenu from './components/NodeContextMenu';
import Breadcrumbs, { NavigationNode } from './components/Breadcrumbs';
import ConnectionPreview from './components/ConnectionPreview';
import ConnectionTypeSelector from './components/ConnectionTypeSelector';
import ConnectionQuickMenu from './components/ConnectionQuickMenu';
import ProjectManager from './components/ProjectManager';
import TemplateGallery from './components/TemplateGallery';
import GridOverlay from './components/GridOverlay';
import SearchPanel from './components/SearchPanel';
import LayersPanel from './components/LayersPanel';
import TemplateCreator from './components/TemplateCreator';
import VersionHistory from './components/VersionHistory';
import ValidationPanel from './components/ValidationPanel';
import Settings from './components/Settings';
import TextLayer from './components/TextLayer';
import { ArchitectureNode, ComponentType, ConnectionType, CustomPort, Project, ArchitectureTemplate, GridSettings, TextElement } from './types';
import { CONNECTION_TYPE_CONFIG } from './constants';
import { useConnectionStyling, getEdgeStyle, getEdgeMarkerStyle } from './hooks/useConnection';
import { generateYaml } from './utils/yamlGenerator';
import { analyzeArchitecture, identifySketch } from './services/geminiService';
import { useUndoRedo } from './hooks/useUndoRedo';
import { saveProject, loadProject, getCurrentProjectId, importProjectFromJSON, exportProjectAsJSON, saveVersion, getVersions, deleteVersion, ProjectVersion } from './utils/projectStorage';
import { exportAsPNG, exportAsSVG, generateThumbnail } from './utils/imageExport';
import { saveUserTemplate } from './utils/templateStorage';
import { alignNodes, snapToGrid, AlignmentType } from './utils/alignmentUtils';
import { applyLayout, LayoutAlgorithm } from './utils/layoutAlgorithms';
import { generateTerraform, CloudProvider } from './utils/terraformGenerator';
import { generateKubernetes } from './utils/kubernetesGenerator';
import { validateArchitecture, ValidationIssue, validateConnection } from './utils/architectureValidator';
import { hasApiToken } from './utils/tokenStorage';
import { saveAs } from 'file-saver';
import { Download, Play, RefreshCw, Loader2, FileCode, X, Wand2, CheckCircle2, AlertTriangle } from 'lucide-react';

const initialNodes: Node[] = [];
const idRandom = () => Math.random().toString(36).substring(2, 9);

// Helper to determine precise connection point (side and percentage) based on click position
const calculateConnectionPoint = (node: Node, flowPos: { x: number; y: number }): { side: 'top' | 'right' | 'bottom' | 'left', percentage: number } => {
  // FIX: Check node.style first for resized dimensions (common in Groups)
  const styleWidth = node.style?.width;
  const styleHeight = node.style?.height;
  
  const width = typeof styleWidth === 'number' ? styleWidth : (node.width ?? (node as any).measured?.width ?? 75);
  const height = typeof styleHeight === 'number' ? styleHeight : (node.height ?? (node as any).measured?.height ?? 25);
  
  const x = node.position.x;
  const y = node.position.y;

  // Relative to node
  const relX = flowPos.x - x;
  const relY = flowPos.y - y;

  // Distances to each edge
  const distTop = Math.abs(relY);
  const distBottom = Math.abs(relY - height);
  const distLeft = Math.abs(relX);
  const distRight = Math.abs(relX - width);

  const min = Math.min(distTop, distBottom, distLeft, distRight);

  let side: 'top' | 'right' | 'bottom' | 'left' = 'top';
  let percentage = 50;

  if (min === distTop) {
     side = 'top';
     percentage = (relX / width) * 100;
  } else if (min === distBottom) {
     side = 'bottom';
     percentage = (relX / width) * 100;
  } else if (min === distLeft) {
     side = 'left';
     percentage = (relY / height) * 100;
  } else {
     side = 'right';
     percentage = (relY / height) * 100;
  }

  // Clamp percentage to keep it visually sane
  percentage = Math.max(5, Math.min(95, percentage));
  
  // Snap to common positions (25%, 50%, 75%) for cleaner connections
  const snapThreshold = 10; // percentage points
  if (Math.abs(percentage - 25) < snapThreshold) {
    percentage = 25;
  } else if (Math.abs(percentage - 50) < snapThreshold) {
    percentage = 50;
  } else if (Math.abs(percentage - 75) < snapThreshold) {
    percentage = 75;
  }

  return { side, percentage };
};

const AppContent = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition, getNodes, getEdges } = useReactFlow(); 
  const updateNodeInternals = useUpdateNodeInternals();
  
  // React Flow State
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  
  // Undo/Redo
  const { takeSnapshot, undo, redo, canUndo, canRedo } = useUndoRedo();

  // App State
  const [tool, setTool] = useState<Tool>('select');
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ id: string; x: number; y: number; edge: Edge } | null>(null);
  const [nodeContextMenu, setNodeContextMenu] = useState<{ nodeId: string; x: number; y: number } | null>(null);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  
  // Text Elements State
  const [textElements, setTextElements] = useState<TextElement[]>([]);
  const [selectedTextElementId, setSelectedTextElementId] = useState<string | null>(null);
  
  // Navigation State for nested architectures
  const [navigationPath, setNavigationPath] = useState<NavigationNode[]>([]);
  const [currentParentNodeId, setCurrentParentNodeId] = useState<string | null>(null);
  const [rootLevelData, setRootLevelData] = useState<{
    nodes: ArchitectureNode[];
    edges: ArchitectureEdge[];
    viewport: { x: number; y: number; zoom: number };
  } | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  
  
  // Define handleNavigateBackWrapper early as a stable function
  // It will call the actual implementation through a ref that gets set later
  const navigateBackFnRef = useRef<((targetIndex: number) => void) | null>(null);
  
  const handleNavigateBackWrapper = useCallback((targetIndex: number) => {
    const fn = navigateBackFnRef.current;
    if (fn) {
      fn(targetIndex);
    } else {
      console.warn('handleNavigateBack not yet initialized');
    }
  }, []);
  
  // AI State
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  
  // Sketch State
  const [processingSketchBounds, setProcessingSketchBounds] = useState<{x: number, y: number, width: number, height: number} | null>(null);

  // Project State
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('Untitled Project');
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [showTemplateCreator, setShowTemplateCreator] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [projectVersions, setProjectVersions] = useState<ProjectVersion[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
  const [highlightedNodeIds, setHighlightedNodeIds] = useState<string[]>([]);
  const [showSettings, setShowSettings] = useState(false);

  // Grid State
  const [gridSettings, setGridSettings] = useState<GridSettings>({
    enabled: false,
    size: 20,
    snapToGrid: false
  });

  // Search State
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Layers State
  interface Layer {
    id: string;
    name: string;
    visible: boolean;
    locked: boolean;
    zIndex: number;
  }
  const [layers, setLayers] = useState<Layer[]>([]);
  const [showLayersPanel, setShowLayersPanel] = useState(false);

  // --- CUSTOM CONNECTION DRAG LOGIC (REFS) ---
  const dragRef = useRef<{
    isActive: boolean;
    nodeId: string | null;
    startX: number;
    startY: number;
    flowStartX: number;
    flowStartY: number;
    isConnecting: boolean; // True if threshold crossed
  }>({
    isActive: false,
    nodeId: null,
    startX: 0,
    startY: 0,
    flowStartX: 0,
    flowStartY: 0,
    isConnecting: false
  });

  // State for rendering the line only (Screen Coordinates)
  const [renderConnectionLine, setRenderConnectionLine] = useState<{ 
    x1: number; 
    y1: number; 
    x2: number; 
    y2: number;
    isValid: boolean;
    sourceNodeId?: string;
    targetNodeId?: string;
  } | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const [pendingEdge, setPendingEdge] = useState<Edge | null>(null);
  const [pendingNodeIds, setPendingNodeIds] = useState<string[]>([]);
  
  // Connection type pre-selection - use string literal to avoid enum initialization issues
  const [selectedConnectionType, setSelectedConnectionType] = useState<ConnectionType>('HTTP' as ConnectionType);
  
  // Quick menu state
  const [quickMenu, setQuickMenu] = useState<{
    edgeId: string;
    x: number;
    y: number;
  } | null>(null);

  // --- DATA UPDATES (defined early for nodeTypes) ---

  const updateNode = useCallback((id: string, updates: Partial<Node> | ((node: Node) => Partial<Node>)) => {
    takeSnapshot(nodes, edges);
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          const newValues = typeof updates === 'function' ? updates(node) : updates;
          const newData = newValues.data ? { ...node.data, ...newValues.data } : node.data;
          return { ...node, ...newValues, data: newData };
        }
        return node;
      })
    );
  }, [nodes, edges, takeSnapshot]);

  const updateEdge = useCallback((id: string, updates: Partial<Edge> | ((edge: Edge) => Partial<Edge>)) => {
    takeSnapshot(nodes, edges);
    setEdges((eds) => 
      eds.map((edge) => {
        if (edge.id === id) {
           const newValues = typeof updates === 'function' ? updates(edge) : updates;
           const newData = newValues.data ? { ...edge.data, ...newValues.data } : edge.data;
           return { ...edge, ...newValues, data: newData };
        }
        return edge;
      })
    );
  }, [nodes, edges, takeSnapshot]);

  // Text Element Handlers
  const handleTextElementCreate = useCallback((element: TextElement) => {
    takeSnapshot(nodes, edges);
    setTextElements((prev) => [...prev, element]);
    setSelectedTextElementId(element.id);
    // Deselect nodes/edges when creating text
    setSelectedNodeIds([]);
    setSelectedEdgeId(null);
    setShowPropertiesPanel(true);
  }, [nodes, edges, takeSnapshot]);

  const handleTextElementUpdate = useCallback((id: string, updates: Partial<TextElement>) => {
    takeSnapshot(nodes, edges);
    setTextElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
    );
  }, [nodes, edges, takeSnapshot]);

  const handleTextElementDelete = useCallback((id: string) => {
    takeSnapshot(nodes, edges);
    setTextElements((prev) => prev.filter((el) => el.id !== id));
    if (selectedTextElementId === id) {
      setSelectedTextElementId(null);
    }
  }, [nodes, edges, takeSnapshot, selectedTextElementId]);

  const nodeTypes = useMemo(() => ({ 
    custom: (props: any) => <CustomNode {...props} connectionToolActive={tool === 'connect'} updateNode={updateNode} />
  }), [tool, updateNode]);

  const edgeTypes = useMemo(() => ({
    default: (props: any) => <CustomEdge {...props} updateEdge={updateEdge} />
  }), [updateEdge]);
  useEffect(() => {
    if (!pendingEdge || pendingNodeIds.length === 0) return;

    const idsToUpdate = [...new Set(pendingNodeIds)];
    requestAnimationFrame(() => {
      idsToUpdate.forEach((id) => updateNodeInternals(id));
      requestAnimationFrame(() => {
        setEdges((eds) => [...eds, pendingEdge]);
        setPendingEdge(null);
        setPendingNodeIds([]);
      });
    });
  }, [pendingEdge, pendingNodeIds, updateNodeInternals, setEdges]);

  // --- PROJECT MANAGEMENT (defined early for keyboard shortcuts) ---

  const handleSaveProject = useCallback(async () => {
    if (!reactFlowInstance || !reactFlowWrapper.current) return;
    
    setSaveStatus('saving');
    const viewport = reactFlowInstance.getViewport();
    
    // If we're in a nested view, save current nested state first
    let nodesToSave = nodes;
    let edgesToSave = edges;
    
    if (currentParentNodeId && rootLevelData) {
      // Save current nested architecture to the parent node (recursively)
      const updateNestedArchitecture = (nodesArray: ArchitectureNode[]): ArchitectureNode[] => {
        return nodesArray.map(n => {
          if (n.id === currentParentNodeId) {
            return {
              ...n,
              data: {
                ...n.data,
                nestedArchitecture: { nodes, edges, viewport }
              }
            };
          }
          if (n.data.nestedArchitecture) {
            return {
              ...n,
              data: {
                ...n.data,
                nestedArchitecture: {
                  ...n.data.nestedArchitecture,
                  nodes: updateNestedArchitecture(n.data.nestedArchitecture.nodes)
                }
              }
            };
          }
          return n;
        });
      };
      
      // Save the root level data with updated nested architecture
      nodesToSave = updateNestedArchitecture(rootLevelData.nodes);
      edgesToSave = rootLevelData.edges;
    }
    
    // Generate thumbnail
    let thumbnail: string | undefined;
    try {
      thumbnail = await generateThumbnail(reactFlowWrapper.current);
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
    }
    
    const savedProject = saveProject(
      projectName, 
      nodesToSave, 
      edgesToSave, 
      currentParentNodeId && rootLevelData ? rootLevelData.viewport : viewport, 
      currentProjectId || undefined, 
      thumbnail,
      textElements
    );
    setCurrentProjectId(savedProject.id);
    setSaveStatus('saved');
  }, [projectName, nodes, edges, textElements, currentProjectId, reactFlowInstance, currentParentNodeId, rootLevelData]);

  const handleSaveAsProject = useCallback(() => {
    if (!reactFlowInstance) return;
    
    const newName = window.prompt('Enter project name:', `${projectName} (Copy)`);
    if (newName) {
      setSaveStatus('saving');
      const viewport = reactFlowInstance.getViewport();
      const savedProject = saveProject(newName, nodes, edges, viewport, undefined, undefined, textElements);
      setCurrentProjectId(savedProject.id);
      setProjectName(newName);
      setSaveStatus('saved');
    }
  }, [projectName, nodes, edges, textElements, reactFlowInstance]);

  const handleExportJSON = useCallback(() => {
    if (!reactFlowInstance) return;

    const viewport = reactFlowInstance.getViewport();
    const project: Project = {
      id: currentProjectId || idRandom(),
      name: projectName,
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      nodes,
      edges,
      viewport
    };

    const json = exportProjectAsJSON(project);
    const blob = new Blob([json], { type: 'application/json' });
    saveAs(blob, `${projectName}.archweaver.json`);
  }, [projectName, nodes, edges, currentProjectId, reactFlowInstance]);

  // Auto Layout handler (defined early for keyboard shortcuts)
  const handleAutoLayout = useCallback((algorithm: LayoutAlgorithm) => {
    if (nodes.length === 0) return;
    
    takeSnapshot(nodes, edges);
    const currentNodes = getNodes();
    const currentEdges = getEdges();
    const laidOutNodes = applyLayout(algorithm, currentNodes, currentEdges);
    setNodes(laidOutNodes);
  }, [nodes, edges, takeSnapshot, getNodes, getEdges, setNodes]);

  // --- NAVIGATION HANDLERS FOR NESTED ARCHITECTURE (defined early for keyboard shortcuts) ---

  const handleOpenInside = useCallback((nodeId: string) => {
    const node = getNodes().find(n => n.id === nodeId);
    if (!node) {
      return;
    }
    
    setIsNavigating(true);
    takeSnapshot(nodes, edges);
    
    const currentViewport = reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 };
    
    // Save current state before navigating
    if (currentParentNodeId) {
      // We're already nested - update the parent node in rootLevelData
      if (rootLevelData) {
        const updateNestedArchitecture = (nodesArray: ArchitectureNode[]): ArchitectureNode[] => {
          return nodesArray.map(n => {
            if (n.id === currentParentNodeId) {
              return {
                ...n,
                data: {
                  ...n.data,
                  nestedArchitecture: { 
                    nodes, 
                    edges, 
                    viewport: currentViewport 
                  }
                }
              };
            }
            if (n.data.nestedArchitecture) {
              return {
                ...n,
                data: {
                  ...n.data,
                  nestedArchitecture: {
                    ...n.data.nestedArchitecture,
                    nodes: updateNestedArchitecture(n.data.nestedArchitecture.nodes)
                  }
                }
              };
            }
            return n;
          });
        };
        
        setRootLevelData({
          ...rootLevelData,
          nodes: updateNestedArchitecture(rootLevelData.nodes)
        });
      }
    } else {
      // Save root level state
      setRootLevelData({
        nodes,
        edges,
        viewport: currentViewport
      });
    }
    
    // Load nested architecture or start fresh
    const nested = node.data.nestedArchitecture;
    if (nested) {
      setNodes(nested.nodes);
      setEdges(nested.edges);
      setTimeout(() => {
        if (reactFlowInstance) {
          reactFlowInstance.setViewport(nested.viewport, { duration: 400 });
        }
        setIsNavigating(false);
      }, 100);
    } else {
      setNodes([]);
      setEdges([]);
      setTimeout(() => {
        if (reactFlowInstance) {
          reactFlowInstance.fitView({ duration: 400 });
        }
        setIsNavigating(false);
      }, 100);
    }
    
    // Update navigation
    setNavigationPath(prev => {
      const newPath = [...prev, { nodeId: node.id, label: node.data.label }];
      return newPath;
    });
    setCurrentParentNodeId(nodeId);
    setNodeContextMenu(null);
    setShowPropertiesPanel(false);
  }, [nodes, edges, currentParentNodeId, reactFlowInstance, rootLevelData, getNodes, takeSnapshot, setNodes, setEdges]);

  const handleNavigateBack = useCallback((targetIndex: number) => {
    setIsNavigating(true);
    takeSnapshot(nodes, edges);
    
    const currentViewport = reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 };
    
    // Helper function to recursively update a node's nested architecture
    const updateNodeNestedArchitecture = (
      nodeArray: ArchitectureNode[],
      targetNodeId: string,
      nestedData: { nodes: ArchitectureNode[]; edges: ArchitectureEdge[]; viewport: { x: number; y: number; zoom: number } }
    ): ArchitectureNode[] => {
      return nodeArray.map(node => {
        if (node.id === targetNodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              nestedArchitecture: nestedData
            }
          };
        }
        if (node.data.nestedArchitecture) {
          return {
            ...node,
            data: {
              ...node.data,
              nestedArchitecture: {
                ...node.data.nestedArchitecture,
                nodes: updateNodeNestedArchitecture(
                  node.data.nestedArchitecture.nodes,
                  targetNodeId,
                  nestedData
                )
              }
            }
          };
        }
        return node;
      });
    };
    
    // Helper function to find a node by ID in the hierarchy
    const findNodeInHierarchy = (
      nodeArray: ArchitectureNode[],
      nodeId: string
    ): ArchitectureNode | null => {
      for (const node of nodeArray) {
        if (node.id === nodeId) {
          return node;
        }
        if (node.data.nestedArchitecture) {
          const found = findNodeInHierarchy(node.data.nestedArchitecture.nodes, nodeId);
          if (found) return found;
        }
      }
      return null;
    };
    
    // Navigate to target level
    if (targetIndex === -1) {
      // Back to root
      if (rootLevelData) {
        // Save current nested state before going to root
        if (currentParentNodeId) {
          const updatedNodes = updateNodeNestedArchitecture(
            rootLevelData.nodes,
            currentParentNodeId,
            { nodes, edges, viewport: currentViewport }
          );
          setRootLevelData({ ...rootLevelData, nodes: updatedNodes });
          setNodes(updatedNodes);
        } else {
          setNodes(rootLevelData.nodes);
        }
        setEdges(rootLevelData.edges);
        setTimeout(() => {
          if (reactFlowInstance) {
            reactFlowInstance.setViewport(rootLevelData.viewport, { duration: 400 });
          }
          setIsNavigating(false);
        }, 100);
      } else {
        // No root data, just clear navigation
        setIsNavigating(false);
      }
      setNavigationPath([]);
      setCurrentParentNodeId(null);
    } else {
      // Navigate to specific breadcrumb level (parent level)
      if (!rootLevelData) {
        setIsNavigating(false);
        return;
      }
      
      const targetNode = navigationPath[targetIndex];
      if (!targetNode) {
        setIsNavigating(false);
        return;
      }
      
      // Save current nested state if we have a current parent
      let nodesToSearch = rootLevelData.nodes;
      if (currentParentNodeId) {
        nodesToSearch = updateNodeNestedArchitecture(
          rootLevelData.nodes,
          currentParentNodeId,
          { nodes, edges, viewport: currentViewport }
        );
        // Update root level data with saved state
        setRootLevelData({ ...rootLevelData, nodes: nodesToSearch });
      }
      
      // Find the target node in the hierarchy
      const targetNodeData = findNodeInHierarchy(nodesToSearch, targetNode.nodeId);
      
      if (targetNodeData?.data.nestedArchitecture) {
        // Load the target node's nested architecture
        setNodes(targetNodeData.data.nestedArchitecture.nodes);
        setEdges(targetNodeData.data.nestedArchitecture.edges);
        setTimeout(() => {
          if (reactFlowInstance) {
            reactFlowInstance.setViewport(targetNodeData.data.nestedArchitecture!.viewport, { duration: 400 });
          }
          setIsNavigating(false);
        }, 100);
      } else {
        // No nested architecture, load empty
        setNodes([]);
        setEdges([]);
        setTimeout(() => {
          if (reactFlowInstance) {
            reactFlowInstance.fitView({ duration: 400 });
          }
          setIsNavigating(false);
        }, 100);
      }
      
      // Update navigation path and current parent
      setNavigationPath(prev => prev.slice(0, targetIndex + 1));
      setCurrentParentNodeId(targetNode.nodeId);
    }
  }, [nodes, edges, currentParentNodeId, navigationPath, reactFlowInstance, rootLevelData, setNodes, setEdges, takeSnapshot]);
  
  // Initialize the ref once handleNavigateBack is defined
  useEffect(() => {
    navigateBackFnRef.current = handleNavigateBack;
  }, [handleNavigateBack]);

  // --- KEYBOARD SHORTCUTS ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Undo: Ctrl+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        const currentNodes = getNodes();
        const currentEdges = getEdges();
        undo(currentNodes, currentEdges, setNodes, setEdges);
      }
      // Redo: Ctrl+Y or Ctrl+Shift+Z
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        e.preventDefault();
        const currentNodes = getNodes();
        const currentEdges = getEdges();
        redo(currentNodes, currentEdges, setNodes, setEdges);
      }
      
      // Group: Ctrl+G
      if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
        e.preventDefault();
        handleGroup();
      }

      // Delete: Backspace or Delete
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Only delete if not editing an input
        if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
          // Delete selected text element if one is selected
          if (selectedTextElementId) {
            e.preventDefault();
            handleTextElementDelete(selectedTextElementId);
          } else {
            handleDeleteSelected();
          }
        }
      }

      // Save: Ctrl+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && !e.shiftKey) {
        e.preventDefault();
        handleSaveProject();
      }

      // Save As: Ctrl+Shift+S
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 's') {
        e.preventDefault();
        handleSaveAsProject();
      }

      // Open Projects: Ctrl+O
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault();
        setShowProjectManager(true);
      }

      // Export Menu: Ctrl+E
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        handleExportJSON();
      }

      // Search: Ctrl+F
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowSearch(true);
      }

      // Auto Layout: Ctrl+L
      if ((e.ctrlKey || e.metaKey) && e.key === 'l') {
        e.preventDefault();
        handleAutoLayout('hierarchical');
      }

      // Escape: Close search
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false);
        setSearchTerm('');
      }

      // Settings: Ctrl+,
      if ((e.ctrlKey || e.metaKey) && e.key === ',') {
        e.preventDefault();
        setShowSettings(true);
      }

      // Navigate back in nested architecture: Backspace (when not in input)
      if (e.key === 'Backspace' && navigationPath.length > 0) {
        if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
          e.preventDefault();
          // Use wrapper function to avoid initialization order issues
          handleNavigateBackWrapper(navigationPath.length - 2); // Go back one level
        }
      }

      // Tool shortcuts
      if (e.key.toLowerCase() === 'h') setTool('hand');
      if (e.key.toLowerCase() === 'v') setTool('select');
      if (e.key.toLowerCase() === 'p') setTool('draw');
      if (e.key.toLowerCase() === 'e' && !e.ctrlKey && !e.metaKey) setTool('eraser');
      if (e.key.toLowerCase() === 'c' && !e.ctrlKey && !e.metaKey) setTool('connect');
      if (e.key.toLowerCase() === 't' && !e.ctrlKey && !e.metaKey) setTool('text');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, setNodes, setEdges, selectedNodeIds, selectedEdgeId, getNodes, getEdges, handleSaveProject, handleSaveAsProject, handleExportJSON, showSearch, handleAutoLayout, navigationPath, handleNavigateBackWrapper]);

  // --- AUTO-SAVE WITH DEBOUNCE ---
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  useEffect(() => {
    // Skip auto-save on initial mount
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }
    
    // Don't auto-save if no project exists
    if (!currentProjectId) return;
    
    // Mark as unsaved when nodes or edges change
    setSaveStatus('unsaved');
    
    // Debounce auto-save: wait 3 seconds after last change
    const autoSaveTimer = setTimeout(() => {
      handleSaveProject();
    }, 3000); // 3 second delay
    
    // Clear timer if nodes/edges change again before auto-save triggers
    return () => clearTimeout(autoSaveTimer);
  }, [nodes, edges, currentProjectId, handleSaveProject, isInitialLoad]); // Trigger when nodes or edges change
  
  // Load versions when project changes
  useEffect(() => {
    if (currentProjectId) {
      setProjectVersions(getVersions(currentProjectId));
    } else {
      setProjectVersions([]);
    }
  }, [currentProjectId]);

  // Load last project on mount
  useEffect(() => {
    const lastProjectId = getCurrentProjectId();
    if (lastProjectId) {
      const project = loadProject(lastProjectId);
      if (project) {
        setIsInitialLoad(true); // Prevent auto-save on initial load
        setNodes(project.nodes);
        setEdges(project.edges);
        setTextElements(project.textElements || []);
        setProjectName(project.name);
        setCurrentProjectId(project.id);
        setSaveStatus('saved');
        if (reactFlowInstance && project.viewport) {
          reactFlowInstance.setViewport(project.viewport);
        }
      }
    }
  }, [reactFlowInstance]);

  // Clean up quick menu if edge is deleted
  useEffect(() => {
    if (quickMenu) {
      const edgeExists = edges.some(e => e.id === quickMenu.edgeId);
      if (!edgeExists) {
        setQuickMenu(null);
      }
    }
  }, [edges, quickMenu]);


  // --- CONNECTION VALIDATION ---
  
  const [connectionWarnings, setConnectionWarnings] = useState<Map<string, string>>(new Map());
  
  const isValidConnection = useCallback((
    sourceId: string, 
    targetId: string, 
    sourceHandle?: string, 
    targetHandle?: string
  ): boolean => {
    // Prevent self-connections
    if (sourceId === targetId) return false;
    
    // Check if target node is locked
    const targetNode = getNodes().find(n => n.id === targetId);
    if (targetNode?.data.locked) return false;
    
    // Check for duplicate connections
    const existingEdge = getEdges().find(e => 
      e.source === sourceId && 
      e.target === targetId &&
      (!sourceHandle || e.sourceHandle === sourceHandle) &&
      (!targetHandle || e.targetHandle === targetHandle)
    );
    
    return !existingEdge;
  }, [getNodes, getEdges]);

  // --- HANDLERS ---

  const onConnect = useCallback(
    (params: Connection) => {
      // Validate connection
      if (!params.source || !params.target) return;
      if (!isValidConnection(params.source, params.target, params.sourceHandle || undefined, params.targetHandle || undefined)) {
        return;
      }
      
      takeSnapshot(nodes, edges); // Save before connect
      
      const edgeStyle = getEdgeStyle(selectedConnectionType, false, false, false);
      const markerStyle = getEdgeMarkerStyle(selectedConnectionType, false);
      const config = CONNECTION_TYPE_CONFIG[selectedConnectionType];
      
      // Validate connection
      const sourceNode = getNodes().find(n => n.id === params.source);
      const targetNode = getNodes().find(n => n.id === params.target);
      let connectionWarning: string | undefined;
      
      if (sourceNode && targetNode) {
        const validation = validateConnection(
          sourceNode.data.type,
          targetNode.data.type,
          selectedConnectionType,
          undefined // Port not available at creation time
        );
        if (validation.warning) {
          connectionWarning = validation.warning;
        }
      }
      
      const newEdge = addEdge({ 
        ...params, 
        animated: true, 
        type: 'default', // Default to Bezier
        label: selectedConnectionType, 
        style: edgeStyle,
        labelStyle: { fill: config?.color || '#000000', fontWeight: 600, fontSize: '10px' },
        labelBgStyle: { fill: '#ffffff', stroke: config?.color || '#000000', strokeWidth: 1 },
        labelBgPadding: [8, 4],
        labelBgBorderRadius: 4,
        markerEnd: markerStyle,
        data: { 
          type: selectedConnectionType,
          ...(connectionWarning ? { warning: connectionWarning } : {})
        }
      }, edges);
      
      setEdges(newEdge);
      
      // Show quick menu
      if (reactFlowInstance && params.source && params.target) {
        const sourceNode = getNodes().find(n => n.id === params.source);
        const targetNode = getNodes().find(n => n.id === params.target);
        if (sourceNode && targetNode) {
          const midX = (sourceNode.position.x + targetNode.position.x) / 2;
          const midY = (sourceNode.position.y + targetNode.position.y) / 2;
          const screenPos = reactFlowInstance.flowToScreenPosition({ x: midX, y: midY });
          
          // Find the newly created edge
          const createdEdge = newEdge.find(e => 
            e.source === params.source && 
            e.target === params.target &&
            (!params.sourceHandle || e.sourceHandle === params.sourceHandle) &&
            (!params.targetHandle || e.targetHandle === params.targetHandle)
          );
          
          if (createdEdge) {
            // Use requestAnimationFrame to ensure edge is in state
            const edgeId = createdEdge.id;
            requestAnimationFrame(() => {
              setTimeout(() => {
                // Double-check edge still exists
                const currentEdges = getEdges();
                if (currentEdges.find(e => e.id === edgeId)) {
                  setQuickMenu({
                    edgeId: edgeId,
                    x: screenPos.x,
                    y: screenPos.y
                  });
                }
              }, 100);
            });
          }
        }
      }
    },
    [setEdges, nodes, edges, takeSnapshot, isValidConnection, selectedConnectionType, reactFlowInstance, getNodes, setQuickMenu]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      
      if (!reactFlowWrapper.current || !reactFlowInstance) {
        console.warn('onDrop: Missing reactFlowWrapper or reactFlowInstance');
        return;
      }

      const type = event.dataTransfer.getData('application/reactflow') as ComponentType;
      if (!type) {
        console.warn('onDrop: No component type in dataTransfer');
        return;
      }

      takeSnapshot(nodes, edges); // Save before drop

      // Use React Flow's screenToFlowPosition which correctly handles:
      // - Viewport offset (pan)
      // - Zoom level
      // - Coordinate transformation
      // It expects viewport coordinates (clientX/clientY)
      let position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      
      // Adjust position to center the node at the drop point
      // Standard nodes are approximately 75px wide and 30px tall
      const nodeWidth = 75;
      const nodeHeight = 30;
      position = {
        x: position.x - nodeWidth / 2,
        y: position.y - nodeHeight / 2,
      };
      
      // Apply snap-to-grid if enabled
      if (gridSettings.snapToGrid) {
        position = {
          x: snapToGrid(position.x, gridSettings.size),
          y: snapToGrid(position.y, gridSettings.size)
        };
      }
      
      const newNode: ArchitectureNode = {
        id: idRandom(),
        type: 'custom',
        position,
        data: { label: type, type: type },
        // Don't use transform in style as it interferes with React Flow positioning
        // Use only opacity for entrance animation
        style: {
          opacity: 0,
          transition: 'opacity 0.3s ease-out',
        },
      };

      setNodes((nds) => nds.concat(newNode));
      
      // Trigger entrance animation - only animate opacity, not transform
      requestAnimationFrame(() => {
        setNodes((nds) => nds.map(n => {
          if (n.id === newNode.id) {
            return { 
              ...n, 
              style: { 
                ...n.style, 
                opacity: 1 
              } 
            };
          }
          return n;
        }));
        
        // Clean up animation style after animation completes
        setTimeout(() => {
          setNodes((nds) => nds.map(node => 
            node.id === newNode.id 
              ? { ...node, style: { ...node.style, transition: undefined } }
              : node
          ));
        }, 300);
      });
    },
    [reactFlowInstance, setNodes, nodes, edges, takeSnapshot, gridSettings]
  );

  const onNodeDragStart = useCallback(() => {
    takeSnapshot(nodes, edges);
  }, [nodes, edges, takeSnapshot]);

  // Handle node position changes with snap-to-grid
  const handleNodesChange = useCallback((changes: any[]) => {
    if (gridSettings.snapToGrid) {
      const updatedChanges = changes.map(change => {
        if (change.type === 'position' && change.position) {
          return {
            ...change,
            position: {
              x: snapToGrid(change.position.x, gridSettings.size),
              y: snapToGrid(change.position.y, gridSettings.size)
            }
          };
        }
        return change;
      });
      onNodesChange(updatedChanges);
    } else {
      onNodesChange(changes);
    }
  }, [gridSettings, onNodesChange]);

  // Alignment handler
  const handleAlign = useCallback((alignment: AlignmentType) => {
    if (selectedNodeIds.length < 2) return;
    
    takeSnapshot(nodes, edges);
    const currentNodes = getNodes();
    const alignedNodes = alignNodes(currentNodes, alignment, selectedNodeIds);
    setNodes(alignedNodes);
  }, [selectedNodeIds, nodes, edges, takeSnapshot, getNodes, setNodes]);

  // Grid handlers
  const handleToggleGrid = useCallback(() => {
    setGridSettings(prev => ({ ...prev, enabled: !prev.enabled }));
  }, []);

  const handleToggleSnapToGrid = useCallback(() => {
    setGridSettings(prev => ({ ...prev, snapToGrid: !prev.snapToGrid }));
  }, []);

  const handleGridSizeChange = useCallback((size: number) => {
    setGridSettings(prev => ({ ...prev, size }));
  }, []);

  // Search handlers
  const handleJumpToNode = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node && reactFlowInstance) {
      // Center viewport on node
      reactFlowInstance.setCenter(node.position.x, node.position.y, { zoom: 1.5, duration: 500 });
      
      // Select the node
      setNodes((nds) => nds.map(n => ({ ...n, selected: n.id === nodeId })));
      setSelectedNodeIds([nodeId]);
      setSelectedEdgeId(null);
    }
  }, [nodes, reactFlowInstance, setNodes]);

  // Layers handlers
  const handleNodeLayerChange = useCallback((nodeId: string, layerId: string | null) => {
    takeSnapshot(nodes, edges);
    setNodes((nds) => nds.map(n => 
      n.id === nodeId ? { ...n, data: { ...n.data, layerId } } : n
    ));
  }, [nodes, edges, takeSnapshot, setNodes]);

  const handleToggleLayerVisibility = useCallback((layerId: string) => {
    setLayers((prev) => prev.map(l => 
      l.id === layerId ? { ...l, visible: !l.visible } : l
    ));
  }, []);

  const handleToggleLayerLock = useCallback((layerId: string) => {
    setLayers((prev) => prev.map(l => 
      l.id === layerId ? { ...l, locked: !l.locked } : l
    ));
  }, []);

  // Filter nodes based on layer visibility
  const visibleNodes = useMemo(() => {
    return nodes.map(node => {
      const layerId = node.data.layerId;
      if (!layerId) return { ...node, hidden: false };
      
      const layer = layers.find(l => l.id === layerId);
      if (!layer) return { ...node, hidden: false };
      
      return { ...node, hidden: !layer.visible };
    });
  }, [nodes, layers]);

  // Apply z-index based on layers
  const nodesWithZIndex = useMemo(() => {
    return visibleNodes.map(node => {
      const layerId = node.data.layerId;
      if (!layerId) return { ...node, style: { ...node.style, zIndex: 0 } };
      
      const layer = layers.find(l => l.id === layerId);
      if (!layer) return { ...node, style: { ...node.style, zIndex: 0 } };
      
      return { 
        ...node, 
        style: { ...node.style, zIndex: layer.zIndex + 1 },
        hidden: !layer.visible
      };
    });
  }, [visibleNodes, layers]);

  // --- CUSTOM CONNECTION & CONTEXT HANDLERS ---
  
  // Note: isValidConnection is defined above in HANDLERS section
  
  const onWrapperMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button === 0 && reactFlowInstance) { 
      const target = event.target as HTMLElement;
      
      const isHandle = target.classList.contains('react-flow__handle');
      const isEdgeZone = target.classList.contains('node-edge') || target.closest('.node-edge');
      
      if (isEdgeZone && !isHandle) {
        const nodeElement = target.closest('.react-flow__node');
        
        if (nodeElement) {
          const nodeId = nodeElement.getAttribute('data-id');
          if (nodeId) {
             const currentNode = getNodes().find(n => n.id === nodeId);
             if (currentNode) {
                event.stopPropagation();
                event.preventDefault();

                const pos = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });

                dragRef.current = {
                  isActive: true,
                  nodeId: currentNode.id,
                  startX: event.clientX,
                  startY: event.clientY,
                  flowStartX: pos.x,
                  flowStartY: pos.y,
                  isConnecting: false
                };
             }
          }
        }
      }
    }
  }, [reactFlowInstance, getNodes]);

  const onPaneMouseMove = useCallback((event: React.MouseEvent) => {
     if (dragRef.current.isActive) {
        const { startX, startY, nodeId } = dragRef.current;
        const dist = Math.sqrt(Math.pow(event.clientX - startX, 2) + Math.pow(event.clientY - startY, 2));

        if (dist > 5) {
            dragRef.current.isConnecting = true;
            
            // Check validity if hovering over a node
            let isValid = true;
            let targetNodeId: string | undefined;
            
            if (hoveredNodeId && nodeId) {
              targetNodeId = hoveredNodeId;
              isValid = isValidConnection(nodeId, hoveredNodeId);
            }
            
            setRenderConnectionLine({
                x1: startX,
                y1: startY,
                x2: event.clientX,
                y2: event.clientY,
                isValid,
                sourceNodeId: nodeId || undefined,
                targetNodeId
            });
        }
     }
  }, [hoveredNodeId, isValidConnection]);

  const onPaneMouseUp = useCallback((event: React.MouseEvent) => {
     const { isActive, isConnecting, nodeId, flowStartX, flowStartY } = dragRef.current;
     
     if (isActive) {
        if (isConnecting && nodeId) {
            const flowPos = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
            
            if (hoveredNodeId && hoveredNodeId !== nodeId) {
                 const startNode = getNodes().find(n => n.id === nodeId);
                 const targetNode = getNodes().find(n => n.id === hoveredNodeId);

                 if (startNode && targetNode) {
                     // Validate connection before creating
                     if (!isValidConnection(nodeId, hoveredNodeId)) {
                       dragRef.current = {
                         isActive: false,
                         nodeId: null,
                         startX: 0,
                         startY: 0,
                         flowStartX: 0,
                         flowStartY: 0,
                         isConnecting: false
                       };
                       setRenderConnectionLine(null);
                       return;
                     }
                     
                     takeSnapshot(nodes, edges);

                     const startPoint = calculateConnectionPoint(startNode, { x: flowStartX, y: flowStartY });
                     const targetPoint = calculateConnectionPoint(targetNode, flowPos);

                     const sourceHandleId = `port-${idRandom()}`;
                     const targetHandleId = `port-${idRandom()}`;

                     setNodes((nds) => nds.map(n => {
                        if (n.id === startNode.id) {
                            return {
                                ...n,
                                data: {
                                    ...n.data,
                                    customPorts: [
                                        ...(n.data.customPorts || []),
                                        { id: sourceHandleId, side: startPoint.side, percentage: startPoint.percentage, handleType: 'source' }
                                    ]
                                }
                            };
                        }
                        if (n.id === targetNode.id) {
                             return {
                                ...n,
                                data: {
                                    ...n.data,
                                    customPorts: [
                                        ...(n.data.customPorts || []),
                                        { id: targetHandleId, side: targetPoint.side, percentage: targetPoint.percentage, handleType: 'target' }
                                    ]
                                }
                            };
                        }
                        return n;
                     }));

                     const edgeStyle = getEdgeStyle(selectedConnectionType, false, false, false);
                     const markerStyle = getEdgeMarkerStyle(selectedConnectionType, false);
                     const config = CONNECTION_TYPE_CONFIG[selectedConnectionType];
                     
                     const newEdge: Edge = {
                        id: `e-${idRandom()}`,
                        source: startNode.id,
                        sourceHandle: sourceHandleId,
                        target: targetNode.id,
                        targetHandle: targetHandleId,
                        animated: true,
                        type: 'default',
                        label: selectedConnectionType,
                        style: edgeStyle,
                        labelStyle: { fill: config?.color || '#000000', fontWeight: 600, fontSize: '10px' },
                        labelBgStyle: { fill: '#ffffff', stroke: config?.color || '#000000', strokeWidth: 1 },
                        labelBgPadding: [8, 4],
                        labelBgBorderRadius: 4,
                        markerEnd: markerStyle,
                        data: { type: selectedConnectionType }
                     };

                     setPendingNodeIds([startNode.id, targetNode.id]);
                     setPendingEdge(newEdge);
                     
                     // Show quick menu after connection is created
                     if (reactFlowInstance) {
                       const sourcePos = reactFlowInstance.getNode(startNode.id)?.position || { x: 0, y: 0 };
                       const targetPos = reactFlowInstance.getNode(targetNode.id)?.position || { x: 0, y: 0 };
                       const midX = (sourcePos.x + targetPos.x) / 2;
                       const midY = (sourcePos.y + targetPos.y) / 2;
                       const screenPos = reactFlowInstance.flowToScreenPosition({ x: midX, y: midY });
                       
                       // Use requestAnimationFrame to ensure edge is in state
                       const edgeId = newEdge.id;
                       requestAnimationFrame(() => {
                         setTimeout(() => {
                           // Double-check edge still exists
                           const currentEdges = getEdges();
                           if (currentEdges.find(e => e.id === edgeId)) {
                             setQuickMenu({
                               edgeId: edgeId,
                               x: screenPos.x,
                               y: screenPos.y
                             });
                           }
                         }, 100);
                       });
                     }
                 }
            }
        } else if (nodeId) {
            setNodes((nds) => nds.map(n => ({
              ...n,
              selected: n.id === nodeId
            })));
            setSelectedNodeIds([nodeId]);
            setSelectedEdgeId(null);
        }

        dragRef.current = {
            isActive: false,
            nodeId: null,
            startX: 0,
            startY: 0,
            flowStartX: 0,
            flowStartY: 0,
            isConnecting: false
        };
        setRenderConnectionLine(null);
     }
  }, [hoveredNodeId, getNodes, nodes, edges, takeSnapshot, reactFlowInstance, setNodes, setEdges, isValidConnection, selectedConnectionType, setQuickMenu]);

  // --- CONTEXT MENU HANDLERS ---

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault(); 
      setNodes((nds) => nds.map((n) => ({
        ...n,
        selected: n.id === node.id,
      })));
      setSelectedNodeIds([node.id]);
      setSelectedEdgeId(null);
      setNodeContextMenu({
        nodeId: node.id,
        x: event.clientX,
        y: event.clientY
      });
    },
    [setNodes]
  );

  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.preventDefault();
      setContextMenu({
        id: edge.id,
        x: event.clientX,
        y: event.clientY,
        edge,
      });
    },
    []
  );

  const handleMenuDelete = () => {
    if (!contextMenu) return;
    takeSnapshot(nodes, edges);
    setEdges((eds) => eds.filter((e) => e.id !== contextMenu.id));
    setContextMenu(null);
  };

  const handleMenuReverse = () => {
    if (!contextMenu) return;
    takeSnapshot(nodes, edges);
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === contextMenu.id) {
          return {
            ...e,
            source: e.target,
            target: e.source,
          };
        }
        return e;
      })
    );
    setContextMenu(null);
  };

  const handleMenuToggleBi = () => {
    if (!contextMenu) return;
    takeSnapshot(nodes, edges);
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === contextMenu.id) {
            const hasStart = !!e.markerStart;
            return {
                ...e,
                markerStart: hasStart ? undefined : { type: MarkerType.ArrowClosed, color: '#000000' },
            };
        }
        return e;
      })
    );
    setContextMenu(null);
  };

  const handleMenuChangeType = (type: string) => {
    if (!contextMenu) return;
    takeSnapshot(nodes, edges);
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === contextMenu.id) {
          return { ...e, type };
        }
        return e;
      })
    );
    setContextMenu(null);
  };

  // --- SELECTION LOGIC ---

  const onSelectionChange = useCallback(({ nodes: selectedNodes, edges: selectedEdges }: OnSelectionChangeParams) => {
    setSelectedNodeIds(selectedNodes.map(n => n.id));
    setSelectedEdgeId(selectedEdges.length > 0 ? selectedEdges[0].id : null);
    
    if (selectedNodes.length === 0 && selectedEdges.length === 0) {
        setShowPropertiesPanel(false);
    }
    if (selectedNodes.length > 0 || selectedEdges.length > 0) {
      setShowAnalysis(false);
    }
  }, []);

  const onNodeClick: NodeMouseHandler = useCallback((event, node) => {
    // Prevent node selection when text tool is active
    if (tool === 'text') {
      return;
    }
    if (tool === 'eraser') {
      if (node.data.locked) return; // Don't erase locked nodes
      takeSnapshot(nodes, edges);
      setNodes((nds) => nds.filter((n) => n.id !== node.id && n.parentNode !== node.id)); 
      setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id));
    }
  }, [tool, setNodes, setEdges, nodes, edges, takeSnapshot]);

  const onEdgeClick = useCallback((_: React.MouseEvent, edge: Edge) => {
    if (tool === 'eraser') {
      takeSnapshot(nodes, edges);
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }
  }, [tool, setEdges, nodes, edges, takeSnapshot]);

  // --- TOOLING LOGIC ---

  const handleDuplicate = () => {
    takeSnapshot(nodes, edges);
    const newNodes: Node[] = [];
    selectedNodeIds.forEach(id => {
      const node = nodes.find(n => n.id === id);
      // Correct check for ComponentType.GROUP from data.type
      if (node && node.data.type !== ComponentType.GROUP) { 
        newNodes.push({
          ...node,
          id: idRandom(),
          position: { x: node.position.x + 50, y: node.position.y + 50 },
          selected: true,
          parentNode: node.parentNode 
        });
      }
    });
    setNodes(nds => {
       const deselected = nds.map(n => ({...n, selected: false}));
       return [...deselected, ...newNodes];
    });
  };

  const handleToggleLock = () => {
    takeSnapshot(nodes, edges);
    setNodes((nds) => nds.map(node => {
      if (selectedNodeIds.includes(node.id)) {
        const isLocked = !node.data.locked;
        return {
          ...node,
          draggable: !isLocked,
          connectable: !isLocked,
          data: { ...node.data, locked: isLocked }
        };
      }
      return node;
    }));
  };

  const handleDeleteSelected = () => {
    if (selectedNodeIds.length === 0 && !selectedEdgeId) return;

    const currentNodes = getNodes(); 

    // Filter out locked nodes from the selection to delete
    const nodesToDelete = currentNodes.filter(n => 
        selectedNodeIds.includes(n.id) && !n.data.locked
    );
    
    if (nodesToDelete.length === 0 && !selectedEdgeId) return; // Nothing eligible to delete

    // Check if any nodes have nested architectures and warn user
    const nodesWithNested = nodesToDelete.filter(n => n.data.nestedArchitecture);
    if (nodesWithNested.length > 0) {
      const nodeNames = nodesWithNested.map(n => n.data.label).join(', ');
      const totalNestedComponents = nodesWithNested.reduce((sum, n) => 
        sum + (n.data.nestedArchitecture?.nodes.length || 0), 0
      );
      
      const message = nodesWithNested.length === 1
        ? `"${nodeNames}" contains ${totalNestedComponents} nested component${totalNestedComponents !== 1 ? 's' : ''}. Deleting it will also remove all nested components. Continue?`
        : `${nodesWithNested.length} components contain nested architectures (${totalNestedComponents} total nested components). Deleting them will remove all nested content. Continue?`;
      
      if (!window.confirm(message)) {
        return; // User cancelled
      }
    }

    takeSnapshot(nodes, edges);
    
    const idsToDelete = new Set(nodesToDelete.map(n => n.id));

    // Delete eligible nodes and their children
    if (idsToDelete.size > 0) {
        setNodes(nds => nds.filter(n => !idsToDelete.has(n.id) && !idsToDelete.has(n.parentNode || '')));
        setEdges(eds => eds.filter(e => 
            !idsToDelete.has(e.source) && 
            !idsToDelete.has(e.target)
        ));
        setSelectedNodeIds([]);
    }
    
    // Delete Edge
    if (selectedEdgeId) {
        setEdges(eds => eds.filter(e => e.id !== selectedEdgeId));
        setSelectedEdgeId(null);
    }
    
    setShowPropertiesPanel(false);
  };

  const handleGroup = () => {
    const currentNodes = getNodes();

    // UNGROUP LOGIC
    const selectedNodes = currentNodes.filter(n => selectedNodeIds.includes(n.id));
    
    if (selectedNodes.length === 1 && selectedNodes[0].data.type === ComponentType.GROUP) {
       takeSnapshot(nodes, edges);
       const groupNode = selectedNodes[0];
       
       setNodes((nds) => {
          const children = nds.filter(n => n.parentNode === groupNode.id);
          
          const updatedChildren = children.map(child => ({
             ...child,
             parentNode: undefined,
             extent: undefined, 
             position: {
                x: groupNode.position.x + child.position.x,
                y: groupNode.position.y + child.position.y
             }
          }));

          const otherNodes = nds.filter(n => n.id !== groupNode.id && n.parentNode !== groupNode.id);
          return [...otherNodes, ...updatedChildren];
       });
       
       setSelectedNodeIds([]); 
       return;
    }

    // GROUP LOGIC
    const validNodesToGroup = selectedNodes.filter(n => !n.parentNode);
    if (validNodesToGroup.length < 1) return;

    takeSnapshot(nodes, edges);

    const minX = Math.min(...validNodesToGroup.map(n => n.position.x));
    const minY = Math.min(...validNodesToGroup.map(n => n.position.y));
    const maxX = Math.max(...validNodesToGroup.map(n => n.position.x + (n.width || 75)));
    const maxY = Math.max(...validNodesToGroup.map(n => n.position.y + (n.height || 50)));

    const padding = 40;
    const groupNode: ArchitectureNode = {
      id: idRandom(),
      type: 'custom',
      position: { x: minX - padding, y: minY - padding },
      style: { 
        width: (maxX - minX) + (padding * 2), 
        height: (maxY - minY) + (padding * 2),
        zIndex: -1 // Ensure it renders behind children
      },
      data: { 
        label: 'Group', 
        type: ComponentType.GROUP 
      },
    };

    setNodes((nds) => {
      const nonSelected = nds.filter(n => !selectedNodeIds.includes(n.id));
      const updatedChildren = validNodesToGroup.map(node => ({
        ...node,
        parentNode: groupNode.id,
        extent: 'parent' as const,
        position: {
          x: node.position.x - groupNode.position.x,
          y: node.position.y - groupNode.position.y
        }
      }));

      // Prepend groupNode so it is first in array (rendered first/behind)
      return [groupNode, ...nonSelected, ...updatedChildren];
    });
    
    setSelectedNodeIds([groupNode.id]);
  };

  const handleSketchComplete = async (imageData: string, bounds: {x: number, y: number, width: number, height: number}) => {
    setProcessingSketchBounds(bounds);
    
    const detectedType = await identifySketch(imageData);
    
    setProcessingSketchBounds(null);

    if (detectedType && reactFlowInstance) {
      takeSnapshot(nodes, edges);

      const centerX = bounds.x + (bounds.width / 2);
      const centerY = bounds.y + (bounds.height / 2);
      
      const position = reactFlowInstance.screenToFlowPosition({
         x: centerX,
         y: centerY
      });

      // Center the node at the sketch position
      const nodeWidth = 75;
      const nodeHeight = 30;
      const centeredPosition = {
        x: position.x - nodeWidth / 2,
        y: position.y - nodeHeight / 2,
      };

      const newNode: ArchitectureNode = {
        id: idRandom(),
        type: 'custom',
        position: centeredPosition, 
        data: { 
          label: detectedType, 
          type: detectedType 
        },
        // Add entrance animation
        style: {
          opacity: 0,
          transition: 'opacity 0.3s ease-out',
        },
      };
      
      setNodes((nds) => nds.concat(newNode));
      
      // Trigger entrance animation
      requestAnimationFrame(() => {
        setNodes((nds) => nds.map(n => {
          if (n.id === newNode.id) {
            return { 
              ...n, 
              style: { 
                ...n.style, 
                opacity: 1 
              } 
            };
          }
          return n;
        }));
        
        // Clean up animation style after animation completes
        setTimeout(() => {
          setNodes((nds) => nds.map(node => 
            node.id === newNode.id 
              ? { ...node, style: { ...node.style, transition: undefined } }
              : node
          ));
        }, 300);
      });

      setNodes((nds) => nds.concat(newNode));
      setTool('select');
    }
  };

  const handleComponentClick = useCallback((componentType: ComponentType) => {
    if (!reactFlowInstance) return;
    
    takeSnapshot(nodes, edges);

    // Get center of the visible canvas area
    const viewport = reactFlowInstance.getViewport();
    let centerPosition = reactFlowInstance.screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    });

    // Add some randomness to avoid stacking components exactly on top of each other
    const randomOffset = () => (Math.random() - 0.5) * 100;
    centerPosition = {
      x: centerPosition.x + randomOffset(),
      y: centerPosition.y + randomOffset()
    };

    // Apply snap-to-grid if enabled
    if (gridSettings.snapToGrid) {
      centerPosition = {
        x: snapToGrid(centerPosition.x, gridSettings.size),
        y: snapToGrid(centerPosition.y, gridSettings.size)
      };
    }

      // Adjust position to center the node
      const nodeWidth = 75;
      const nodeHeight = 30;
      const adjustedPosition = {
        x: centerPosition.x - nodeWidth / 2,
        y: centerPosition.y - nodeHeight / 2,
      };
      
      const newNode: ArchitectureNode = {
        id: idRandom(),
        type: 'custom',
        position: adjustedPosition,
        data: { 
          label: componentType, 
          type: componentType 
        },
        // Don't use transform in style as it interferes with React Flow positioning
        // Use only opacity for entrance animation
        style: {
          opacity: 0,
          transition: 'opacity 0.3s ease-out',
        },
      };

    setNodes((nds) => nds.concat(newNode));
    
    // Trigger entrance animation - only animate opacity, not transform
    requestAnimationFrame(() => {
      setNodes((nds) => nds.map(n => {
        if (n.id === newNode.id) {
          return { 
            ...n, 
            style: { 
              ...n.style, 
              opacity: 1 
            } 
          };
        }
        return n;
      }));
      
      // Clean up animation style after animation completes
      setTimeout(() => {
        setNodes((nds) => nds.map(node => 
          node.id === newNode.id 
            ? { ...node, style: { ...node.style, transition: undefined } }
            : node
        ));
      }, 300);
    });
  }, [reactFlowInstance, nodes, edges, takeSnapshot, setNodes, gridSettings]);

  // --- PROJECT MANAGEMENT ---

  const handleLoadProject = useCallback((project: Project) => {
    setIsInitialLoad(true); // Prevent auto-save when loading
    setNodes(project.nodes);
    setEdges(project.edges);
    setTextElements(project.textElements || []);
    setProjectName(project.name);
    setCurrentProjectId(project.id);
    setSaveStatus('saved');
    
    // Reset navigation state when loading a project
    setNavigationPath([]);
    setCurrentParentNodeId(null);
    setRootLevelData(null);
    setSelectedTextElementId(null);
    
    if (reactFlowInstance && project.viewport) {
      setTimeout(() => {
        reactFlowInstance.setViewport(project.viewport);
      }, 100);
    }
  }, [setNodes, setEdges, reactFlowInstance]);

  const handleNewProject = useCallback(() => {
    if (nodes.length > 0 || edges.length > 0) {
      if (!window.confirm('Start a new project? Unsaved changes will be lost.')) {
        return;
      }
    }
    
    takeSnapshot(nodes, edges);
    setIsInitialLoad(true); // Prevent auto-save when creating new project
    setNodes([]);
    setEdges([]);
    setTextElements([]);
    setProjectName('Untitled Project');
    setCurrentProjectId(null);
    setSaveStatus('saved');
    setAnalysisResult(null);
    setSelectedNodeIds([]);
    setSelectedTextElementId(null);
    setSelectedEdgeId(null);
    
    // Reset navigation state
    setNavigationPath([]);
    setCurrentParentNodeId(null);
    setRootLevelData(null);
  }, [nodes, edges, takeSnapshot, setNodes, setEdges]);

  // --- EXPORT HANDLERS ---

  const handleExportPNG = useCallback(async () => {
    const element = reactFlowWrapper.current;
    if (!element) return;

    try {
      await exportAsPNG(element, {
        fileName: `${projectName}.png`,
        includeBackground: true,
        scale: 2
      });
    } catch (error) {
      console.error('PNG export failed:', error);
      alert('Failed to export PNG. Please try again.');
    }
  }, [projectName]);

  const handleExportSVG = useCallback(async () => {
    const element = reactFlowWrapper.current;
    if (!element) return;

    try {
      await exportAsSVG(element, {
        fileName: `${projectName}.svg`,
        includeBackground: true
      });
    } catch (error) {
      console.error('SVG export failed:', error);
      alert('Failed to export SVG. Please try again.');
    }
  }, [projectName]);

  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.archweaver.json';
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonString = event.target?.result as string;
          const project = importProjectFromJSON(jsonString);
          
          if (project) {
            handleLoadProject(project);
            alert(`Project "${project.name}" imported successfully!`);
          } else {
            alert('Failed to import project. Invalid file format.');
          }
        } catch (error) {
          console.error('Import failed:', error);
          alert('Failed to import project. Please check the file.');
        }
      };
      
      reader.readAsText(file);
    };
    
    input.click();
  }, [handleLoadProject]);

  const handleLoadTemplate = useCallback((template: ArchitectureTemplate) => {
    takeSnapshot(nodes, edges);
    setNodes(template.nodes);
    setEdges(template.edges);
    setProjectName(template.name);
    setCurrentProjectId(null); // New project from template
    setSaveStatus('unsaved');
  }, [nodes, edges, takeSnapshot, setNodes, setEdges]);

  const handleSaveAsTemplate = useCallback(async (templateData: {
    name: string;
    description: string;
    category: 'web' | 'microservices' | 'serverless' | 'data' | 'kubernetes';
    difficulty: 'beginner' | 'intermediate' | 'advanced';
  }) => {
    if (!reactFlowWrapper.current) return;
    
    await saveUserTemplate(
      templateData.name,
      templateData.description,
      templateData.category,
      templateData.difficulty,
      nodes,
      edges,
      reactFlowWrapper.current
    );
  }, [nodes, edges, reactFlowInstance]);

  // Version history handlers
  const handleRestoreVersion = useCallback((version: ProjectVersion) => {
    takeSnapshot(nodes, edges);
    setNodes(version.nodes);
    setEdges(version.edges);
    if (reactFlowInstance && version.viewport) {
      setTimeout(() => {
        reactFlowInstance.setViewport(version.viewport);
      }, 100);
    }
    setSaveStatus('unsaved');
  }, [nodes, edges, takeSnapshot, setNodes, setEdges, reactFlowInstance]);

  const handleDeleteVersion = useCallback((versionId: string) => {
    if (currentProjectId) {
      deleteVersion(currentProjectId, versionId);
      setProjectVersions(getVersions(currentProjectId));
    }
  }, [currentProjectId]);

  const handleCreateVersion = useCallback(async () => {
    if (!currentProjectId || !reactFlowInstance || !reactFlowWrapper.current) return;
    
    const name = window.prompt('Version name (optional):');
    const description = window.prompt('Version description (optional):');
    const viewport = reactFlowInstance.getViewport();
    
    let thumbnail: string | undefined;
    try {
      thumbnail = await generateThumbnail(reactFlowWrapper.current);
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
    }
    
    saveVersion(currentProjectId, nodes, edges, viewport, name || undefined, description || undefined, thumbnail);
    setProjectVersions(getVersions(currentProjectId));
  }, [currentProjectId, nodes, edges, reactFlowInstance]);


  const handleAnalyze = async () => {
    // Check if API key is configured
    if (!hasApiToken()) {
      setShowSettings(true);
      alert('Please configure your Gemini API key in Settings to use AI features.');
      return;
    }
    
    setIsAnalyzing(true);
    setShowAnalysis(true);
    setAnalysisResult(null);
    const yaml = generateYaml(nodes, edges);
    const result = await analyzeArchitecture(yaml);
    setAnalysisResult(result);
    setIsAnalyzing(false);
    
    // If result contains API key error, show settings
    if (result.includes('API Key Error') || result.includes('API key')) {
      setTimeout(() => {
        if (window.confirm('API key issue detected. Would you like to configure it now?')) {
          setShowSettings(true);
        }
      }, 500);
    }
  };

  // Validation handler
  const handleValidate = useCallback(() => {
    const issues = validateArchitecture(nodes, edges);
    setValidationIssues(issues);
    setShowValidation(true);
  }, [nodes, edges]);

  // Highlight nodes
  const handleHighlightNodes = useCallback((nodeIds: string[]) => {
    setHighlightedNodeIds(nodeIds);
    setNodes((nds) => nds.map(n => ({
      ...n,
      selected: nodeIds.includes(n.id)
    })));
  }, [setNodes]);

  const handleClearHighlight = useCallback(() => {
    setHighlightedNodeIds([]);
    setNodes((nds) => nds.map(n => ({ ...n, selected: false })));
  }, [setNodes]);

  const handleDownloadYaml = () => {
    const yaml = generateYaml(nodes, edges);
    const blob = new Blob([yaml], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'architecture.yml';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportTerraform = useCallback((provider: CloudProvider) => {
    if (nodes.length === 0) {
      alert('No components to export');
      return;
    }

    const terraform = generateTerraform(nodes, edges, provider);
    const blob = new Blob([terraform], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName}_${provider}.tf`;
    a.click();
    URL.revokeObjectURL(url);
  }, [nodes, edges, projectName]);

  const handleExportKubernetes = useCallback(() => {
    if (nodes.length === 0) {
      alert('No components to export');
      return;
    }

    const namespace = window.prompt('Kubernetes namespace:', 'default') || 'default';
    const k8sYaml = generateKubernetes(nodes, edges, namespace, 'single');
    
    if (typeof k8sYaml === 'string') {
      const blob = new Blob([k8sYaml], { type: 'text/yaml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName}_k8s.yaml`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [nodes, edges, projectName]);

  const selectedNode = useMemo(() => 
    selectedNodeIds.length === 1 ? nodes.find((n) => n.id === selectedNodeIds[0]) || null : null, 
  [nodes, selectedNodeIds]);

  const selectedEdge = useMemo(() => 
    edges.find((e) => e.id === selectedEdgeId) || null,
  [edges, selectedEdgeId]);

  const hasSelection = !!(selectedNode || selectedEdge || selectedTextElementId);

  // Use connection styling hook for better performance and maintainability
  const styledEdges = useConnectionStyling(edges, hoveredEdgeId, selectedEdgeId);

  const isSelectionLocked = useMemo(() => {
    if (selectedNodeIds.length === 0) return false;
    return nodes.filter(n => selectedNodeIds.includes(n.id)).every(n => n.data.locked);
  }, [nodes, selectedNodeIds]);

  return (
    <div 
        className={`flex h-screen w-screen bg-white overflow-hidden text-black ${tool === 'hand' ? 'cursor-grab active:cursor-grabbing' : ''}`}
        onContextMenu={(e) => {
            if (dragRef.current.isActive) e.preventDefault();
        }}
    >
      <Sidebar onComponentClick={handleComponentClick} />
      
      <div 
        className="flex-1 relative h-full" 
        ref={reactFlowWrapper}
        onMouseMove={onPaneMouseMove}
        onMouseUp={onPaneMouseUp}
        onMouseDownCapture={onWrapperMouseDown}
      >
        <Toolbar 
          currentTool={tool} 
          setTool={setTool}
          onDuplicate={handleDuplicate}
          onToggleLock={handleToggleLock}
          onGroup={handleGroup}
          onUndo={() => {
             const currentNodes = getNodes();
             const currentEdges = getEdges();
             undo(currentNodes, currentEdges, setNodes, setEdges)
          }}
          onRedo={() => {
             const currentNodes = getNodes();
             const currentEdges = getEdges();
             redo(currentNodes, currentEdges, setNodes, setEdges)
          }}
          hasSelection={selectedNodeIds.length > 0}
          isLocked={isSelectionLocked}
          canUndo={canUndo}
          canRedo={canRedo}
          onSave={handleSaveProject}
          onSaveAs={handleSaveAsProject}
          onOpenProjects={() => setShowProjectManager(true)}
          onNewProject={handleNewProject}
          onExportPNG={handleExportPNG}
          onExportSVG={handleExportSVG}
          onExportJSON={handleExportJSON}
          onExportYAML={handleDownloadYaml}
          onImportJSON={handleImportJSON}
          onOpenTemplates={() => setShowTemplateGallery(true)}
          saveStatus={saveStatus}
          onAlign={handleAlign}
          gridEnabled={gridSettings.enabled}
          onToggleGrid={handleToggleGrid}
          snapToGrid={gridSettings.snapToGrid}
          onToggleSnapToGrid={handleToggleSnapToGrid}
          onGridSizeChange={handleGridSizeChange}
          onOpenSearch={() => setShowSearch(true)}
          onAutoLayout={handleAutoLayout}
          onOpenLayers={() => setShowLayersPanel(true)}
          onSaveAsTemplate={() => setShowTemplateCreator(true)}
          onExportTerraform={handleExportTerraform}
          onExportKubernetes={handleExportKubernetes}
          onOpenSettings={() => setShowSettings(true)}
        />

        <GridOverlay 
          enabled={gridSettings.enabled}
          size={gridSettings.size}
        />

        <SearchPanel
          isOpen={showSearch}
          onClose={() => {
            setShowSearch(false);
            setSearchTerm('');
          }}
          nodes={nodes}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onJumpToNode={handleJumpToNode}
        />

        <DrawingCanvas 
          isActive={tool === 'draw'} 
          onSketchComplete={handleSketchComplete} 
        />

        <TextLayer
          textElements={textElements}
          selectedTextElementId={selectedTextElementId}
          onTextElementCreate={handleTextElementCreate}
          onTextElementUpdate={handleTextElementUpdate}
          onTextElementDelete={handleTextElementDelete}
          onTextElementSelect={(id) => {
            setSelectedTextElementId(id);
            if (id) {
              setShowPropertiesPanel(true);
              // Deselect nodes/edges when selecting text
              setSelectedNodeIds([]);
              setSelectedEdgeId(null);
            }
          }}
          tool={tool}
          gridSettings={gridSettings}
        />
        
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            onDelete={handleMenuDelete}
            onReverse={handleMenuReverse}
            onToggleBidirectional={handleMenuToggleBi}
            onChangeType={handleMenuChangeType}
            isBidirectional={!!contextMenu.edge.markerStart}
            currentType={contextMenu.edge.type || 'default'}
          />
        )}

        {nodeContextMenu && (() => {
          const node = nodes.find(n => n.id === nodeContextMenu.nodeId);
          if (!node) {
            return null;
          }
          
          return (
            <NodeContextMenu
              x={nodeContextMenu.x}
              y={nodeContextMenu.y}
              onClose={() => setNodeContextMenu(null)}
              onDelete={() => {
                handleDeleteSelected();
                setNodeContextMenu(null);
              }}
              onDuplicate={() => {
                handleDuplicate();
                setNodeContextMenu(null);
              }}
              onToggleLock={() => {
                handleToggleLock();
                setNodeContextMenu(null);
              }}
              onOpenProperties={() => {
                setShowPropertiesPanel(true);
                setNodeContextMenu(null);
              }}
              onOpenInside={() => {
                handleOpenInside(nodeContextMenu.nodeId);
              }}
              isLocked={!!node.data.locked}
              hasNestedArchitecture={!!node.data.nestedArchitecture}
              nestedComponentCount={node.data.nestedArchitecture?.nodes?.length || 0}
            />
          );
        })()}

        {processingSketchBounds && (
          <div 
            className="absolute z-[60] flex items-center justify-center pointer-events-none"
            style={{
              left: processingSketchBounds.x,
              top: processingSketchBounds.y,
              width: processingSketchBounds.width,
              height: processingSketchBounds.height
            }}
          >
             <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-2 border border-black/10 animate-scale-in will-change-transform" style={{ animationDuration: '200ms' }}>
                <Loader2 className="animate-spin text-black" size={16} style={{ animationDuration: '1s' }} />
                <span className="text-xs font-bold text-black whitespace-nowrap animate-pulse-slow">Interpreting...</span>
             </div>
          </div>
        )}

        {renderConnectionLine && (
          <ConnectionPreview
            x1={renderConnectionLine.x1}
            y1={renderConnectionLine.y1}
            x2={renderConnectionLine.x2}
            y2={renderConnectionLine.y2}
            isValid={renderConnectionLine.isValid}
            connectionType={selectedConnectionType}
            sourceNodeId={renderConnectionLine.sourceNodeId}
            targetNodeId={renderConnectionLine.targetNodeId}
          />
        )}
        
        {quickMenu && (() => {
          const edge = edges.find(e => e.id === quickMenu.edgeId);
          if (!edge) {
            // Edge was deleted, dismiss menu - handled by useEffect below
            return null;
          }
          
          return (
            <ConnectionQuickMenu
              key={edge.id}
              x={quickMenu.x}
              y={quickMenu.y}
              connectionId={edge.id}
              connectionType={edge.data?.type || ConnectionType.HTTP}
              isBidirectional={!!edge.markerStart}
              onTypeChange={(type) => {
                takeSnapshot(nodes, edges);
                const config = CONNECTION_TYPE_CONFIG[type];
                setEdges((eds) => eds.map(e => {
                  if (e.id === edge.id) {
                    const markerStyle = getEdgeMarkerStyle(type, !!e.markerStart);
                    return { 
                      ...e, 
                      label: type, 
                      data: { ...e.data, type },
                      labelStyle: { ...e.labelStyle, fill: config?.color || '#000000' },
                      labelBgStyle: { ...e.labelBgStyle, stroke: config?.color || '#000000' },
                      markerEnd: markerStyle,
                      markerStart: e.markerStart ? { ...markerStyle, color: config?.color || '#000000' } : undefined,
                    };
                  }
                  return e;
                }));
              }}
              onToggleBidirectional={() => {
                takeSnapshot(nodes, edges);
                const connectionType = edge.data?.type || ConnectionType.HTTP;
                const config = CONNECTION_TYPE_CONFIG[connectionType];
                const markerStyle = getEdgeMarkerStyle(connectionType, !edge.markerStart);
                setEdges((eds) => eds.map(e => {
                  if (e.id === edge.id) {
                    return { 
                      ...e, 
                      markerStart: e.markerStart 
                        ? undefined 
                        : { ...markerStyle, color: config?.color || '#000000' }
                    };
                  }
                  return e;
                }));
              }}
              onDelete={() => {
                takeSnapshot(nodes, edges);
                setEdges((eds) => eds.filter(e => e.id !== edge.id));
                setQuickMenu(null);
              }}
              onDismiss={() => setQuickMenu(null)}
            />
          );
        })()}

        {/* Navigation Loading Overlay */}
        {isNavigating && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[55] bg-white/80 backdrop-blur-sm animate-fade-in" style={{ animationDuration: '200ms' }}>
            <div className="bg-white border-2 border-purple-300 rounded-lg p-6 shadow-xl flex flex-col items-center gap-3 animate-scale-in" style={{ animationDuration: '300ms' }}>
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-200 border-t-purple-600" style={{ animationDuration: '1s' }}></div>
              <p className="text-sm font-medium text-gray-700 animate-pulse-slow">Navigating...</p>
            </div>
          </div>
        )}

        {/* Empty State for Nested Architecture */}
        {navigationPath.length > 0 && nodes.length === 0 && !isNavigating && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[50]">
            <div className="bg-gradient-to-br from-purple-50 via-white to-blue-50 border-2 border-dashed border-purple-300 rounded-xl p-10 text-center max-w-lg shadow-lg">
              <div className="text-purple-400 mb-4">
                <svg className="w-20 h-20 mx-auto animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Design Internal Architecture</h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                You're designing the internal architecture of <span className="font-bold text-purple-700">{navigationPath[navigationPath.length - 1]?.label}</span>
              </p>
              <div className="bg-white/80 rounded-lg p-4 border border-purple-200">
                <p className="text-xs text-gray-700 font-medium mb-2">How to get started:</p>
                <ul className="text-xs text-gray-600 text-left space-y-1.5 list-disc list-inside">
                  <li>Drag components from the left sidebar onto the canvas</li>
                  <li>Connect components using the connection tool (C key)</li>
                  <li>Right-click components to access more options</li>
                  <li>Use breadcrumbs above to navigate back to parent levels</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <ReactFlow
          nodes={nodesWithZIndex.filter(n => !n.hidden)}
          edges={styledEdges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          onNodeContextMenu={onNodeContextMenu} 
          onNodeMouseEnter={(_, node) => {
            setHoveredNodeId(node.id);
            // Add visual feedback when hovering during connection drag
            if (dragRef.current.isActive && dragRef.current.nodeId) {
              const isValid = isValidConnection(dragRef.current.nodeId, node.id);
              // Visual feedback is handled by ConnectionPreview component
            }
          }}
          onNodeMouseLeave={() => setHoveredNodeId(null)}
          onEdgeClick={onEdgeClick}
          onEdgeContextMenu={onEdgeContextMenu}
          onEdgeMouseEnter={(_, edge) => setHoveredEdgeId(edge.id)}
          onEdgeMouseLeave={() => setHoveredEdgeId(null)}
          onSelectionChange={onSelectionChange}
          onPaneClick={(e) => {
            // Don't close quick menu if clicking inside it
            const target = e.target as HTMLElement;
            if (target.closest('[class*="z-[110]"]') || target.closest('[class*="z-\\[110\\]"]')) {
              return;
            }
            // Don't deselect if clicking on a text element
            if (target.closest('.text-element')) {
              return;
            }
            setContextMenu(null);
            setQuickMenu(null);
            // Deselect text element when clicking on empty canvas
            setSelectedTextElementId(null);
          }}
          onNodeDragStart={onNodeDragStart} 
          
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          connectionMode={ConnectionMode.Loose} 
          
          selectionOnDrag={tool === 'select' && !dragRef.current.isActive}
          panOnDrag={tool === 'hand' ? true : [1, 2]} 
          panOnScroll={true}
          zoomOnScroll={true}
          fitView
          className={`bg-white ${tool === 'connect' ? 'cursor-crosshair' : tool === 'text' ? 'cursor-text' : ''}`}
        >
          <Background color="#e5e5e5" variant={BackgroundVariant.Dots} gap={24} size={1} />
          <Controls className="bg-white border-black text-black fill-black z-[60]" style={{ border: '1px solid #e5e5e5', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} />
          <MiniMap 
            className="bg-white border border-black/10 z-[60]" 
            style={{ 
              bottom: 20, 
              right: 20,
              width: 150,
              height: 100
            }}
            nodeColor={(node) => {
              if (node.data?.type === 'Group') return '#e5e5e5';
              return '#000000';
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
          />
          
          <Panel position="top-left" className="flex gap-3 z-[60]">
            {/* Connection Type Selector - shown when connection tool is active */}
            {tool === 'connect' && (
              <ConnectionTypeSelector
                selectedType={selectedConnectionType}
                onTypeChange={setSelectedConnectionType}
              />
            )}
            
            {/* Breadcrumbs - shown when navigating nested architecture */}
            {navigationPath.length > 0 && (
              <Breadcrumbs
                path={navigationPath}
                onNavigate={handleNavigateBackWrapper}
              />
            )}
            
            {/* Project Name Editor - only show at root level */}
            {navigationPath.length === 0 && (
            <div className="bg-white border border-black/10 px-4 py-2 rounded-md shadow-sm flex items-center gap-2">
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="text-sm font-medium text-black bg-transparent outline-none min-w-[150px] max-w-[250px]"
                placeholder="Project Name"
              />
              <span className={`text-xs font-medium ${
                saveStatus === 'saved' ? 'text-green-600' : 
                saveStatus === 'saving' ? 'text-blue-600' : 
                'text-orange-600'
              }`}>
                {saveStatus === 'saved' ? '✓' : saveStatus === 'saving' ? '...' : '*'}
              </span>
            </div>
            )}

            <button
              onClick={() => {
                takeSnapshot(nodes, edges);
                setNodes([]);
                setEdges([]);
                setAnalysisResult(null);
                setSelectedNodeIds([]);
                setSelectedEdgeId(null);
              }}
              className="bg-white hover:bg-gray-50 text-red-600 border border-red-200 px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all duration-150 shadow-sm hover:scale-105 active:scale-95"
            >
              <RefreshCw size={16} />
              Reset
            </button>
            {!hasApiToken() && (
              <button
                onClick={() => setShowSettings(true)}
                className="bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-300 px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all duration-150 shadow-[3px_3px_0px_0px_rgba(251,146,60,0.2)] hover:scale-105 active:shadow-none active:translate-x-[1px] active:translate-y-[1px] active:scale-95"
                title="Configure your Gemini API key to use AI features"
              >
                <AlertTriangle size={16} />
                API Key Required
              </button>
            )}
            <button
              onClick={handleAnalyze}
              disabled={nodes.length === 0 || isAnalyzing || !hasApiToken()}
              className="bg-black hover:bg-gray-800 text-white border border-black px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all duration-150 shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)] hover:scale-105 active:shadow-none active:translate-x-[1px] active:translate-y-[1px] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              title={!hasApiToken() ? "Configure API key in Settings first" : "Analyze architecture with AI"}
            >
              {isAnalyzing ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} fill="currentColor" />}
              AI Analyze
            </button>
            <button
              onClick={handleValidate}
              disabled={nodes.length === 0}
              className="bg-white hover:bg-gray-50 text-black border border-black px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all duration-150 shadow-[3px_3px_0px_0px_rgba(0,0,0,0.1)] hover:scale-105 active:shadow-none active:translate-x-[1px] active:translate-y-[1px] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <CheckCircle2 size={16} />
              Validate
            </button>
            <button
              onClick={handleDownloadYaml}
              disabled={nodes.length === 0}
              className="bg-white hover:bg-gray-50 text-black border border-black px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all duration-150 shadow-[3px_3px_0px_0px_rgba(0,0,0,0.1)] hover:scale-105 active:shadow-none active:translate-x-[1px] active:translate-y-[1px] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Download size={16} />
              Export YAML
            </button>
          </Panel>
          
          {showAnalysis && (
             <Panel position="bottom-center" className="w-[600px] max-w-[90vw] mb-8 z-[60]">
                <div className="bg-white border-2 border-black rounded-lg shadow-[8px_8px_0px_0px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col max-h-[400px] animate-slide-in-up will-change-transform" style={{ animationDuration: '300ms' }}>
                  <div className="p-4 border-b border-black flex justify-between items-center bg-gray-50">
                    <div className="flex items-center gap-2 text-black font-bold">
                       <FileCode size={18} />
                       Architecture Audit
                    </div>
                    <button onClick={() => setShowAnalysis(false)} className="text-gray-500 hover:text-black">
                      <X size={18} />
                    </button>
                  </div>
                  <div className="p-6 overflow-y-auto text-black text-sm leading-relaxed">
                     {isAnalyzing ? (
                       <div className="flex flex-col items-center justify-center py-8 space-y-4 animate-fade-in">
                          <Loader2 size={32} className="animate-spin text-black" style={{ animationDuration: '1s' }} />
                          <p className="text-gray-500 font-medium animate-pulse-slow">Processing specifications...</p>
                       </div>
                     ) : (
                       <div 
                         className="prose prose-sm max-w-none prose-headings:font-bold prose-headings:text-black prose-p:text-gray-800 prose-strong:text-black prose-ul:list-disc prose-li:marker:text-black"
                         dangerouslySetInnerHTML={{ 
                           __html: analysisResult 
                              ? analysisResult
                                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                  .replace(/### (.*?)\n/g, '<h3 class="text-lg mt-4 mb-2 border-b border-gray-100 pb-1">$1</h3>')
                                  .replace(/- (.*?)\n/g, '<li class="ml-4">$1</li>')
                                  .replace(/\n/g, '<br />') 
                              : 'No analysis available.' 
                         }} 
                       />
                     )}
                  </div>
                </div>
             </Panel>
          )}
        </ReactFlow>

        <ValidationPanel
          isOpen={showValidation}
          onClose={() => {
            setShowValidation(false);
            handleClearHighlight();
          }}
          issues={validationIssues}
          onHighlightNodes={handleHighlightNodes}
          onClearHighlight={handleClearHighlight}
        />
      </div>

      {showPropertiesPanel && (hasSelection || selectedTextElementId) && (
        <PropertiesPanel 
          selectedNode={selectedNode} 
          selectedEdge={selectedEdge}
          selectedTextElement={textElements.find(te => te.id === selectedTextElementId) || null}
          updateNode={updateNode} 
          updateEdge={updateEdge}
          updateTextElement={handleTextElementUpdate}
          onDelete={handleDeleteSelected}
          onDeleteTextElement={() => {
            if (selectedTextElementId) {
              handleTextElementDelete(selectedTextElementId);
            }
          }}
          onOpenInside={handleOpenInside}
          onClose={() => {
            setNodes((nds) => nds.map(n => ({ ...n, selected: false })));
            setEdges((eds) => eds.map(e => ({ ...e, selected: false })));
            setSelectedNodeIds([]);
            setSelectedEdgeId(null);
            setSelectedTextElementId(null);
            setShowPropertiesPanel(false);
          }}
        />
      )}

      <LayersPanel
        isOpen={showLayersPanel}
        onClose={() => setShowLayersPanel(false)}
        nodes={nodes}
        layers={layers}
        onLayersChange={setLayers}
        onNodeLayerChange={handleNodeLayerChange}
        onToggleLayerVisibility={handleToggleLayerVisibility}
        onToggleLayerLock={handleToggleLayerLock}
      />

      <ProjectManager
        isOpen={showProjectManager}
        onClose={() => setShowProjectManager(false)}
        onLoadProject={handleLoadProject}
        onNewProject={handleNewProject}
        currentProjectId={currentProjectId || undefined}
        onOpenVersionHistory={(project) => {
          setCurrentProjectId(project.id);
          setProjectVersions(getVersions(project.id));
          setShowProjectManager(false);
          setShowVersionHistory(true);
        }}
      />

      <VersionHistory
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        project={currentProjectId ? { id: currentProjectId, name: projectName, created: '', modified: '', nodes, edges, viewport: reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 } } : null}
        versions={projectVersions}
        onRestore={handleRestoreVersion}
        onDeleteVersion={handleDeleteVersion}
      />

      <TemplateGallery
        isOpen={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        onSelectTemplate={handleLoadTemplate}
      />

      <TemplateCreator
        isOpen={showTemplateCreator}
        onClose={() => setShowTemplateCreator(false)}
        nodes={nodes}
        edges={edges}
        onSave={handleSaveAsTemplate}
      />

      <Settings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
};

const App = () => (
  <ReactFlowProvider>
    <AppContent />
  </ReactFlowProvider>
);

export default App;